import React from 'react'
import { Flex, Image, Text, FlexProps, useMatchBreakpoints, TokenPairImage } from 'vvs-uikit'
import styled from 'styled-components'
import useTheme from 'hooks/useTheme'

import { useActiveFarmsPeriodicFetching, useFinishedFarmsPeriodicFetching } from 'state/farms/farmsPeriodicFetching'
import { useDeserializedFarms } from 'state/farms/useDeserializedFarms'
import { useFarmTokensPricesUsdc } from 'state/farms/useFarmTokensPricesUsdc'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import BigNumber from 'bignumber.js'
import { getFarmAprByVersion } from 'utils/apr'
import { getAddress } from 'utils/addressHelpers'
import { DeserializedFarm } from 'state/types'
import Balance from 'components/Balance'
import { useFetchPublicPoolsData } from 'state/pools/hooks'
import { isBlindMode } from 'utils'
import { getBalanceAmount } from 'utils/formatBalance'
import { getLpUsdcPrice } from 'utils/farmsPriceHelpers'
import getTokenImageByAddress from 'utils/getTokenImageByAddress'
import { useGetTopGlitterMine } from '../hooks/useGetTopGlitterMine'

type TopItemProps = {
  title: string
  primarySrc: string
  secondarySrc: string
  name: string
  percent: number
  type: string
}

const CenterFlex = styled(Flex)`
  justify-content: center;
  align-content: center;
  align-items: center;
`
const PoolInfoModule = ({
  name,
  primarySrc,
  secondarySrc,
  percent,
  title,
  type,
  ...restProps
}: TopItemProps & FlexProps) => {
  const { isDesktop } = useMatchBreakpoints()

  return (
    <CenterFlex
      paddingTop={['10px', '10px', 0, 0]}
      flexDirection={['column', 'column', 'column', 'row']}
      {...restProps}
    >
      <CenterFlex marginBottom={['10px', '10px', '10px', '0px']}>
        <Image
          src="/images/darkPurpleVector.svg"
          height={24}
          width={24}
          style={{
            minWidth: '24px',
            minHeight: '24px',
          }}
        />
        <Text
          color="black"
          fontSize={isDesktop ? '16px' : '13px'}
          fontWeight="600"
          ml="10px"
          mr="15px"
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {title}:
        </Text>
      </CenterFlex>
      <CenterFlex mr={['0px', '0px', '0px', '15px']} width={32} height={32}>
        <TokenPairImage
          mt={1}
          variant="inverted"
          primarySrc={primarySrc}
          secondarySrc={secondarySrc}
          width={32}
          height={32}
        />
      </CenterFlex>
      <CenterFlex mr={['0px', '0px', '0px', '20px']}>
        <Text
          color="black"
          fontSize={isDesktop ? '16px' : '13px'}
          fontWeight="600"
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Text>
      </CenterFlex>
      <CenterFlex>
        <Text mt={-2} fontSize="28px" fontWeight="600" color="blue">
          {isBlindMode() ? (
            '---'
          ) : (
            <Balance
              decimals={percent > 0 ? 2 : 0}
              fontSize={isDesktop ? '24px' : '16px'}
              bold
              display="inline-block"
              unit="%"
              color="blue"
              lineHeight="1.1"
              value={percent}
            />
          )}
          <Text
            as="sup"
            ml="3px"
            fontSize={isDesktop ? '13px' : '8px'}
            verticalAlign="super"
            fontWeight="400"
            color="blue"
          >
            {type}
          </Text>
        </Text>
      </CenterFlex>
    </CenterFlex>
  )
}

const TopBlingFarmAndCrystalPool: React.FC = () => {
  const { theme } = useTheme()
  const farmsLP = useDeserializedFarms((s) => s.value.farms)
  const tokenPrices = useFarmTokensPricesUsdc((s) => s.value)
  const vvsPrice = usePriceVvsUsdc()

  useActiveFarmsPeriodicFetching()
  useFinishedFarmsPeriodicFetching()

  let maxFarmApr = 0
  let maxFarm: DeserializedFarm
  const activeFarms = farmsLP.filter((farm) => farm.pid !== 0 && farm.multiplier !== '0X')
  activeFarms.forEach((farm) => {
    if (farm.lpTotalSupply.gt(0) && farm.lpTotalInQuoteToken.gt(0)) {
      const totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)

      const lpTokenPrice: BigNumber = getLpUsdcPrice(farm)
      const stakedLiquidityInUSD =
        farm.version === 2 && farm.migration
          ? getBalanceAmount(farm.lpTokenAmountInCraftsmanV2).times(lpTokenPrice)
          : totalLiquidity

      let apr = 0
      const { tokenRewardsApr, lpRewardsApr } = getFarmAprByVersion(
        farm.poolWeight,
        vvsPrice,
        totalLiquidity,
        getAddress(farm.lpAddresses),
        farm.version,
        farm.rewardData,
        stakedLiquidityInUSD,
        tokenPrices,
      )
      apr = tokenRewardsApr + lpRewardsApr

      if (apr > maxFarmApr) {
        maxFarmApr = apr
        maxFarm = farm
      }
    }
  })

  useFetchPublicPoolsData()
  const topPool = useGetTopGlitterMine()
  return (
    <CenterFlex
      flexDirection="row"
      minHeight="84px"
      height={['auto', 'auto', 'auto', '84px']}
      background={theme.colors.gradients.cardHeader}
    >
      {maxFarm && (
        <PoolInfoModule
          marginRight={['10px', '10px', '10px', '10px', '10px', '80px']}
          title="Top Crystal Farm"
          primarySrc={getTokenImageByAddress(maxFarm.token.address)}
          secondarySrc={getTokenImageByAddress(maxFarm.quoteToken.address)}
          name={maxFarm.lpSymbol && maxFarm.lpSymbol.split(' ')[0].toUpperCase()}
          percent={maxFarmApr}
          type="APR"
        />
      )}
      {topPool && (
        <PoolInfoModule
          marginRight="0px"
          title="Top Glitter Mine"
          primarySrc={getTokenImageByAddress(topPool.primarySrc)}
          secondarySrc={getTokenImageByAddress(topPool.secondarySrc)}
          name={topPool.symbol}
          percent={topPool.percent}
          type={topPool.type}
        />
      )}
    </CenterFlex>
  )
}

export default TopBlingFarmAndCrystalPool
